import { motion } from 'framer-motion';
import { House } from '../../data/houses';

interface GlassTubeProps {
    house: House;
    rank: number;
    maxPoints: number;
}

export default function GlassTube({ house, maxPoints }: GlassTubeProps) {
    const fillPercentage = Math.max(8, (house.points / maxPoints) * 100);

    return (
        <div className="flex flex-col items-center group relative z-10 w-32 md:w-40 lg:w-48">
            {/* Main Tube Structure */}
            <div className="relative h-96 lg:h-[28rem] w-16 md:w-20 lg:w-24">

                {/* 1. Base/Stand */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-24 md:w-28 h-6 bg-black/40 rounded-[50%] blur-sm pointer-events-none" />
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 md:w-24 h-4 rounded-[50%] border border-white/10 bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-md shadow-lg" />

                {/* 2. Glass Container Back Layer */}
                <div className="absolute inset-0 rounded-full border border-white/20 bg-gradient-to-r from-white/5 via-transparent to-white/10 backdrop-blur-[1px]"
                    style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)' }}
                />

                {/* 3. Liquid Column */}
                <div className="absolute inset-[3px] bottom-[3px] rounded-full overflow-hidden z-20 flex flex-col justify-end">
                    <motion.div
                        initial={{ height: '0%' }}
                        animate={{ height: `${fillPercentage}%` }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        className="w-full relative"
                        style={{
                            background: house.gemGradient,
                            boxShadow: `0 0 30px ${house.color}80`
                        }}
                    >
                        {/* Bubbles */}
                        <div className="absolute inset-0 w-full h-full overflow-hidden">
                            {[...Array(8)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute bg-white rounded-full opacity-40 mix-blend-overlay"
                                    style={{
                                        width: Math.random() * 6 + 3,
                                        height: Math.random() * 6 + 3,
                                        left: `${Math.random() * 80 + 10}%`,
                                    }}
                                    animate={{
                                        bottom: ['-10%', '110%'],
                                        x: [0, Math.random() * 10 - 5],
                                        opacity: [0, 0.6, 0]
                                    }}
                                    transition={{
                                        repeat: Infinity,
                                        duration: 3 + Math.random() * 4,
                                        delay: Math.random() * 3,
                                        ease: "linear"
                                    }}
                                />
                            ))}
                        </div>
                        {/* Surface Meniscus */}
                        <div className="absolute top-0 w-full h-3 bg-white/40 blur-[1px] transform -translate-y-1/2 rounded-[50%]" />
                        <div className="absolute top-0 w-full h-1 bg-white/60 transform -translate-y-1/2 rounded-[50%]" />
                    </motion.div>
                </div>

                {/* 4. Glass Container Front Reflections (The "Look") */}
                <div className="absolute inset-0 z-30 rounded-full pointer-events-none border-[1px] border-white/30 overflow-hidden">
                    {/* Left strong reflection */}
                    <div className="absolute top-[2%] bottom-[2%] left-[10%] w-[12%] bg-gradient-to-b from-white/10 via-white/40 to-white/10 blur-[1px] opacity-80 rounded-full" />
                    {/* Right subtle reflection */}
                    <div className="absolute top-[5%] bottom-[5%] right-[15%] w-[5%] bg-white/20 blur-[2px] opacity-50 rounded-full" />
                    {/* Top rim highlight */}
                    <div className="absolute top-[2px] left-[15%] right-[15%] h-[3px] bg-white/60 blur-[1px] rounded-full" />
                </div>
            </div>

            {/* Info / Label */}
            <div className="mt-8 flex flex-col items-center space-y-2">
                <h2 className="text-xl md:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white to-white/60 tracking-wider uppercase drop-shadow-sm">
                    {house.name}
                </h2>

                <div className="flex flex-col items-center">
                    <span className="text-3xl md:text-4xl font-bold text-white tracking-tight"
                        style={{ textShadow: `0 0 20px ${house.color}` }}>
                        {house.points.toLocaleString()}
                    </span>
                    <span className="text-xs font-semibold text-white/50 tracking-[0.2em] uppercase">Points</span>
                </div>
            </div>
        </div>
    );
}
